import React, { useRef, useState } from 'react';
import { Play, Pause, Volume2, VolumeX, Tv, Radio, Mail, Globe, Smartphone, MonitorPlay } from 'lucide-react';
import { Button } from '../ui/button';
import { usePreApproval } from '../../pages/LandingPage';

const CinematicAdSystem = () => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const { openPreApproval } = usePreApproval();

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const channels = [
    { icon: Tv, label: "TV & Streaming" },
    { icon: Smartphone, label: "Social Media" },
    { icon: Globe, label: "Google & SEO" },
    { icon: Mail, label: "Email & Direct Mail" },
    { icon: Radio, label: "Radio & Podcasts" },
    { icon: MonitorPlay, label: "500+ TV Channels" },
  ];

  return (
    <section className="py-20 md:py-32 bg-slate-950 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-emerald-500/40 to-transparent" />
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-emerald-500/40 to-transparent" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-4 py-2 mb-6">
            <Tv className="w-4 h-4 text-emerald-400" />
            <span className="text-emerald-400 text-sm font-medium">Powered by AI Horse-Power</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            THE ONE FORCE LIVE
            <span className="text-emerald-400 block">CINEMATIC AD SYSTEM™</span>
          </h2>
          
          <p className="text-xl md:text-2xl text-slate-300 max-w-4xl mx-auto mb-4 italic">
            Your Brand. Everywhere. All At Once.
          </p>
          
          <p className="text-lg text-slate-400 max-w-4xl mx-auto leading-relaxed">
            On TV. On Social. On Google. On Blogs. On Funnels. On Emails. On Direct Mail. On Radio. On Digital Billboards. And on 500+ TV Channels — All Powered by AI Horse-Power.
            <span className="text-emerald-400 font-semibold"> And Covered by $120,000 in Co-op Support!</span>
          </p>
        </div>

        {/* Video Section */}
        <div className="relative max-w-5xl mx-auto mb-16">
          {/* Video Glow */}
          <div className="absolute -inset-4 bg-gradient-to-r from-emerald-500/20 via-cyan-500/20 to-emerald-500/20 rounded-3xl blur-2xl opacity-60" />
          
          {/* Video Container */}
          <div className="relative rounded-2xl overflow-hidden border-2 border-emerald-500/30 shadow-2xl shadow-emerald-500/10 bg-slate-900">
            <video
              ref={videoRef}
              className="w-full aspect-video object-cover"
              poster="https://customer-assets.emergentagent.com/job_7626a04e-dabc-4736-ac34-e6d8b2aebead/artifacts/58zgoikm_ChatGPT%20Image%20Dec%209%2C%202025%2C%2008_07_22%20PM.png"
              muted={isMuted}
              loop
              playsInline
              onClick={togglePlay}
            >
              <source 
                src="https://customer-assets.emergentagent.com/job_7626a04e-dabc-4736-ac34-e6d8b2aebead/artifacts/cdtxm14u_Digital%20Kings%20Rise%20-%20VEED%20Export-1765499895023%20%281%29.mp4" 
                type="video/mp4" 
              />
              Your browser does not support the video tag.
            </video>
            
            {/* Play Overlay */}
            {!isPlaying && (
              <div 
                className="absolute inset-0 flex items-center justify-center bg-slate-950/40 cursor-pointer group"
                onClick={togglePlay}
              >
                <div className="w-24 h-24 bg-emerald-500 rounded-full flex items-center justify-center shadow-2xl shadow-emerald-500/50 group-hover:scale-110 transition-transform">
                  <Play className="w-10 h-10 text-slate-950 ml-1" fill="currentColor" />
                </div>
              </div>
            )}
            
            {/* Video Controls */}
            <div className="absolute bottom-4 right-4 flex items-center space-x-2">
              <button
                onClick={togglePlay}
                className="w-10 h-10 bg-slate-900/80 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-slate-800 transition-colors"
              >
                {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
              </button>
              <button
                onClick={toggleMute}
                className="w-10 h-10 bg-slate-900/80 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-slate-800 transition-colors"
              >
                {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="text-center max-w-4xl mx-auto mb-12">
          <p className="text-lg text-slate-300 leading-relaxed mb-6">
            The One Force Live <span className="text-emerald-400 font-semibold">CINEMATIC AD SYSTEM™</span> is the most powerful advertising engine ever built for small and medium businesses.
          </p>
          <p className="text-lg text-slate-400 leading-relaxed mb-6">
            It puts your brand everywhere customers look — from television screens to social feeds, from Google searches to email inboxes, from your website funnel to your billboard and radio ads.
          </p>
          <p className="text-xl text-white font-semibold">
            Your competitors can't touch this. Your market has never seen anything like it. And your business gets an <span className="text-emerald-400">unfair advantage</span> that lets you dominate your category.
          </p>
        </div>

        {/* Channel Icons */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4 max-w-4xl mx-auto mb-12">
          {channels.map((channel, index) => (
            <div 
              key={index}
              className="flex flex-col items-center p-4 bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl hover:border-emerald-500/30 transition-colors group"
            >
              <div className="w-12 h-12 bg-slate-700 group-hover:bg-emerald-500/20 rounded-full flex items-center justify-center mb-2 transition-colors">
                <channel.icon className="w-6 h-6 text-emerald-400" />
              </div>
              <span className="text-slate-300 text-xs text-center font-medium">{channel.label}</span>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center">
          <Button
            onClick={openPreApproval}
            size="lg"
            data-testid="cinematic-ad-cta-btn"
            className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold px-10 py-6 text-lg rounded-xl transition-all hover:shadow-xl hover:shadow-emerald-500/25 group"
          >
            Get Your Cinematic Ad System
            <Tv className="ml-2 w-5 h-5 group-hover:scale-110 transition-transform" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default CinematicAdSystem;
